import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useIsDark } from '@/hooks/useIsDark';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { useUserContext } from '@/contexts/UserContext';
import { MOCK_ACTIONS } from '@/data/mockCatalog';
import {
  SAFE_DEMO_USER,
  pbLevelIndex,
  safeConfidence,
} from '@/data/mockSafeData';
import { USE_MOCK } from '@/api/client';
import type { ActionItem } from '@/api/types';
import { useAnalyticsPersonal } from '@/hooks/useAnalytics';
import { usePersonalGrowth } from '@/hooks/usePersonalGrowth';
import { PageSpinner } from '@/components/common/PageSpinner';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { InitialsAvatar } from '@/components/common';
import { reportApiError } from '@/utils/errorReporter';
import { useActions } from '@/hooks/useActions';
import { useDashboardStats } from '@/hooks/usePendingInstances';
import { useMyAnswered } from '@/hooks/useMyAnswered';
import { useFundamentals } from '@/hooks/useFundamentals';
import { useTips } from '@/hooks/useTips';
import { formatScore } from '@/utils/formatScore';
import { FeedbackDetailPopup } from './components/FeedbackDetailPopup';
import { FundamentalIcon } from '@/components/common/FundamentalIcon';
import { GivenFeedbackList } from './components/GivenFeedbackList';
import { JourneyChart } from './components/JourneyChart';
import { RadarChart } from './components/RadarChart';
import { SafeEmptyState } from './components/SafeEmptyState';
import { SafeErrorState } from './components/SafeErrorState';
import { KudosCarousel } from './components/KudosCarousel';
import { useSafeScores } from './hooks/useSafeScores';
import { buildFeedbackItems, type FeedbackItem } from './utils/buildFeedbackItems';
import { buildNarrative, selectNarrativeGrowth } from './utils/buildNarrative';
import { buildRadarAxes } from './utils/buildRadarAxes';
import { resolveDailyChallenge } from './utils/dailyChallenge';
import { evaluateDashboardGate } from './utils/dashboardGate';
import { celebrationPhraseKey, resolveCelebrationDelta, resolveTopAttitude } from './utils/celebration';
import { buildRadarData, buildRadarOptions } from './utils/radarConfig';
import { formatDelta, greetingKey, trendIndicator } from './utils/scoreDisplay';
import './SafeView.css';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Derived from the current date — not hardcoded
const now           = new Date();
const CURRENT_MONTH = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
const pastDate      = new Date(now.getFullYear(), now.getMonth() - 3, 1);
const PAST_MONTH    = `${pastDate.getFullYear()}-${String(pastDate.getMonth() + 1).padStart(2, '0')}`;

// Named thresholds for score quality bands
const SCORE_HIGH_THRESHOLD = 3.2;
const SCORE_LOW_THRESHOLD  = 2.8;

/** Ancho de la ventana móvil cuando las fechas no están disponibles todavía. */
const DEFAULT_WINDOW_DAYS = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Número de días entre `startDate` y `endDate`, para interpolarlo en la etiqueta de la araña. */
function windowDaySpan(window: { startDate: string; endDate: string }): number {
  const spanMs = new Date(window.endDate).getTime() - new Date(window.startDate).getTime();
  const days = Math.round(spanMs / MS_PER_DAY);
  return Number.isFinite(days) && days > 0 ? days : DEFAULT_WINDOW_DAYS;
}

// ── Main component ─────────────────────────────────────────────────────────
export function SafeView() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { demoUser } = useUserContext();
  const [popupItem, setPopupItem] = useState<FeedbackItem | null>(null);
  const [ctaCommitted, setCtaCommitted] = useState(false);
  const userId = SAFE_DEMO_USER.id;

  // ── API hooks (only active in real mode; mock mode uses local helpers) ──────
  const personalQuery = useAnalyticsPersonal(demoUser.userId);
  const growthQuery   = usePersonalGrowth(demoUser.userId);
  const statsQuery    = useDashboardStats();
  const catalogQuery  = useFundamentals();
  const answeredQuery = useMyAnswered(demoUser.userId);
  const { data: actionsData = [] } = useActions();
  const { data: tipsData = [] }    = useTips();

  const personalData = personalQuery.data;
  const growthData   = growthQuery.data;
  const dashStats    = statsQuery.data;
  const catalogData  = catalogQuery.data ?? [];

  // ── Derived score data (mock y API real normalizados en el hook) ─────────
  const {
    fundAvgs, accumulatedFundAvgs, totalEvals, currentAvg, pastAvg, pastFundAvgs,
    attitudeAvgs, pastAttitudeAvgs,
    todayWindow, pastWindow, progressPct, answeredCount, totalCount,
  } = useSafeScores({
    personalData,
    growthData,
    dashStats,
    currentMonth: CURRENT_MONTH,
    pastMonth: PAST_MONTH,
    mockUserId: userId,
  });

  // ── Actions (daily challenge + growth cards) ──────────────────────────────
  const allActions = USE_MOCK
    ? MOCK_ACTIONS
    : (actionsData as unknown as typeof MOCK_ACTIONS);

  const levelLabels = [t('safe.level0'), t('safe.level1'), t('safe.level2'), t('safe.level3')] as const;

  const accumulatedSorted = [...accumulatedFundAvgs]
    .sort((first, second) => second.avg - first.avg);
  const challengeCandidates = accumulatedSorted
    .filter(item => item.avg < SCORE_LOW_THRESHOLD)
    .slice(0, 2);

  // Lista aplanada de comportamientos en orden de catálogo, para el Acto 03.
  const feedbackItems = buildFeedbackItems(personalData, catalogData, lang);

  const byScore    = [...feedbackItems].sort((first, second) => second.score - first.score);
  const strengths  = byScore.filter(item => item.score >= SCORE_HIGH_THRESHOLD).slice(0, 3);
  // Las tarjetas de crecimiento siempre muestran los 4 más bajos, sin filtro de umbral.
  const growthList = [...feedbackItems].sort((first, second) => first.score - second.score).slice(0, 4);
  // La narrativa de "siguiente nivel" solo debe aparecer cuando algo está realmente
  // bajo — antes recibía growthList completo y sugería mejorar un comportamiento en 3.7.
  const narrativeGrowth = selectNarrativeGrowth(
    growthList.map(item => ({ name: item.name, avg: item.score })), SCORE_LOW_THRESHOLD,
  );

  const narrative = buildNarrative(
    strengths.map(item => ({ name: item.name, avg: item.score })),
    narrativeGrowth,
    currentAvg, pastAvg, t as (key: string, opts?: Record<string, unknown>) => string,
    levelLabels,
  );

  const { fundamentalId: challengeKey, action } = resolveDailyChallenge(
    accumulatedFundAvgs, challengeCandidates, accumulatedSorted, allActions);
  const challengeFund = catalogData.find(item => item.id === challengeKey);
  const challengeIcon = challengeFund?.icon ?? 'fa-bolt';

  const diff = currentAvg > 0 && pastAvg > 0 ? Math.round((currentAvg - pastAvg) * 10) / 10 : 0;
  const trend      = trendIndicator(diff);
  const trendText  = t(trend.textKey);

  // Fallback de celebración a nivel fundamental cuando no hay comportamiento top
  // (resolveTopAttitude no encontró uno); usa el mismo ranking que el reto diario.
  const topFund = accumulatedSorted[0];

  // Radar chart axes — fundamental o sus comportamientos, según tenga o no (garantizado
  // cargado por el guard de abajo).
  const radarAxes = buildRadarAxes(catalogData, fundAvgs, pastFundAvgs, attitudeAvgs, pastAttitudeAvgs, lang);
  const radarLabels = radarAxes.map(axis => axis.name);
  const radarIcons = radarAxes.map(axis => axis.icon);
  const currentData = radarAxes.map(axis => axis.currentAvg);
  const pastData = radarAxes.map(axis => axis.pastAvg);

  const isDark = useIsDark();

  const radarData = buildRadarData({
    labels: radarLabels,
    currentData,
    pastData,
    currentLabel: t('safe.today', { days: windowDaySpan(todayWindow) }),
    pastLabel: t('safe.threeMonthsAgo', { days: windowDaySpan(pastWindow) }),
    isDark,
  });
  const radarOptions = buildRadarOptions(isDark);

  const topFundObj = catalogData.find(f => f.id === topFund?.id);

  // Se celebra el comportamiento (nivel 2 del catálogo), no el fundamental: nombra una
  // conducta concreta. Si el catálogo no define ese nivel, se recae en el fundamental.
  const topAttitude  = resolveTopAttitude(personalData, catalogData);
  const celebPhrase  = t(celebrationPhraseKey(topAttitude?.code));
  const celebName    = topAttitude?.name ?? topFundObj?.shortNames?.[lang]
    ?? topFundObj?.shortName ?? topFundObj?.names?.[lang] ?? topFund?.id ?? '';
  const celebScore   = topAttitude?.score ?? topFund?.avg ?? 0;
  const celebColor   = topAttitude?.color ?? topFundObj?.color ?? '#342F82';

  // Delta del propio nivel celebrado, ventana "hoy" contra ventana "hace 3 meses" —
  // ambas del mismo ancho. Antes se restaba el acumulado de toda la vida (topFund)
  // menos un promedio de periodo (pastFundAvgs): dos magnitudes distintas.
  const delta    = resolveCelebrationDelta({
    topAttitude,
    topFundamentalId: topFund?.id,
    todayWindow: growthData?.today,
    pastWindow: growthData?.threeMonthsAgo,
  });
  const deltaStr = formatDelta(delta);

  const firstName = (demoUser.nombre || SAFE_DEMO_USER.name).split(' ')[0];
  const greeting = t(greetingKey(new Date().getHours()));

  /**
   * Reintenta solo las consultas del dashboard, sin recargar la página completa.
   * El resultado no se espera: cada consulta actualiza su propio estado y la vista
   * vuelve a renderizarse sola.
   */
  const retryDashboardQueries = () => {
    Promise.all([
      personalQuery.refetch(),
      growthQuery.refetch(),
      statsQuery.refetch(),
      catalogQuery.refetch(),
    ]).catch(refetchError => {
      reportApiError('safeView.retry', refetchError);
    });
  };

  // ── Estado del dashboard: cargando, error, bajo umbral o listo ───────────
  // El catálogo siempre debe resolver desde la API antes de renderizar; nunca se
  // cae a un catálogo hardcodeado.
  const gate = evaluateDashboardGate({
    isLoading: catalogQuery.isLoading
      || (!USE_MOCK && (personalQuery.isLoading || growthQuery.isLoading || statsQuery.isLoading)),
    hasError: !USE_MOCK
      && (personalQuery.isError || growthQuery.isError || statsQuery.isError || catalogQuery.isError),
    answeredSurveys: answeredCount,
    receivedEvaluations: totalEvals,
  });

  if (gate.status === 'error') {
    return (
      <SafeErrorState
        onRetry={retryDashboardQueries}
        isRetrying={personalQuery.isFetching || growthQuery.isFetching || statsQuery.isFetching}
      />
    );
  }

  if (gate.status === 'loading') {
    return (
      <div className="ud-wrap">
        <PageSpinner minHeight="120px" />
      </div>
    );
  }

  if (gate.status === 'below-threshold') {
    return <SafeEmptyState catalogData={catalogData} lang={lang} gate={gate} />;
  }

  return (
    <div className="ud-wrap">

      {/* ── HEADER ── */}
      <div className="ud-header-wrap">
        <div className="ud-persona-wave" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 378.88 381.37" className="ud-persona-wave-svg">
            <g>
              <path fill="#332f81" d="M158.67,143.98c36.98,0,66.95-29.97,66.95-66.95S195.65,10.08,158.67,10.08s-66.95,29.97-66.95,66.95,29.97,66.95,66.95,66.95Z"/>
              <g>
                <ellipse fill="#fff" cx="143.89" cy="71.29" rx="7.92" ry="7.92">
                  <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </ellipse>
                <ellipse fill="#fff" cx="173.43" cy="71.29" rx="7.92" ry="7.92">
                  <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </ellipse>
                <path d="M136.5,68 Q143.89,59 151.28,68" stroke="#fff" strokeWidth="2.8" fill="none" strokeLinecap="round" opacity="0">
                  <animate attributeName="opacity" values="0;0;0;0;0;1;1;1;1;0;0" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </path>
                <path d="M166,68 Q173.43,59 180.86,68" stroke="#fff" strokeWidth="2.8" fill="none" strokeLinecap="round" opacity="0">
                  <animate attributeName="opacity" values="0;0;0;0;0;1;1;1;1;0;0" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </path>
              </g>
              <path fill="#ee5294" d="M160.27,152.99c25.15.79,45.27,21.42,45.27,46.73,0,19.37-11.79,36-28.59,43.1-5.31,2.28-11.13,3.58-17.24,3.71,31.24.5,56.51,26.07,56.51,57.42l44.97-110.85c-26.58-24.59-62.02-39.73-100.92-40.11Z"/>
              <path fill="#ef8421" d="M121.42,227.88c-5.91-7.83-9.41-17.58-9.41-28.14,0-25.82,20.95-46.77,46.77-46.77.5,0,.99.01,1.49.02-.5-.02-1-.04-1.5-.04-83.26,0-150.98,67.72-150.98,150.98,0,23.38,17.23,43.15,40.38,46.35l110.95,15.33c2.17.3,4.35.45,6.47.45h0c22.97-.01,43.02-16.97,46.27-40.39,3.55-25.6-14.33-49.19-39.93-52.74l-55.56-7.69c10.22-11.19,24.79-18.32,41.01-18.71-14.72-.4-27.73-7.63-35.96-18.65Z"/>
              <path fill="#ee5294" d="M112.01,199.74c0,10.56,3.5,20.31,9.41,28.14-5.79-7.75-9.22-17.37-9.22-27.79,0-25.66,20.81-46.47,46.47-46.47s46.47,20.81,46.47,46.47c0,19.17-11.61,35.63-28.19,42.73,16.79-7.1,28.59-23.73,28.59-43.1,0-25.32-20.12-45.94-45.27-46.73-.5,0-.99-.02-1.49-.02-25.82,0-46.77,20.95-46.77,46.77Z"/>
              <path fill="#ef8421" d="M112.01,199.74c0,10.56,3.5,20.31,9.41,28.14-5.79-7.75-9.22-17.37-9.22-27.79,0-25.66,20.81-46.47,46.47-46.47s46.47,20.81,46.47,46.47c0,19.17-11.61,35.63-28.19,42.73,16.79-7.1,28.59-23.73,28.59-43.1,0-25.32-20.12-45.94-45.27-46.73-.5,0-.99-.02-1.49-.02-25.82,0-46.77,20.95-46.77,46.77Z"/>
              <path fill="#e11f26" d="M205.14,200.09c0-25.66-20.81-46.47-46.47-46.47s-46.47,20.81-46.47,46.47c0,10.42,3.43,20.04,9.22,27.79,8.46,11.22,21.85,18.5,36.97,18.62.13,0,.25-.01.38-.01,6.45,0,12.59-1.31,18.18-3.67,16.57-7.1,28.19-23.56,28.19-42.73ZM181.54,202.79l-22.82,21.03-22.9-21.03c-6.04-6.05-6.05-15.84,0-21.88,6.05-6.04,15.84-6.04,21.88,0,.35.35.67.73.97,1.1.3-.38.62-.75.97-1.1,6.05-6.04,15.84-6.04,21.88,0h.02c6.04,6.05,6.04,15.84,0,21.88Z"/>
              <path fill="#fff" d="M181.52,180.91h0,0c-6.04-6.04-15.83-6.04-21.88,0-.35.35-.67.72-.97,1.1-.3-.37-.62-.75-.97-1.1-6.04-6.04-15.83-6.04-21.88,0-6.05,6.04-6.04,15.83,0,21.88l22.9,21.03,22.82-21.03c6.04-6.04,6.04-15.83,0-21.88h-.02Z"/>
              <g transform="translate(0,18)">
                <path fill="#ee5294" d="M313.9,79.23c-23.6-10.52-51.27.07-61.78,23.67l-21.48,48.17-44.97,110.85c0,22.08,15.43,41.16,37.03,45.75,3.27.7,6.54,1.02,9.76,1.02h0v.03c18.15,0,35.06-10.61,42.7-27.74l62.41-139.97c10.52-23.6-.07-51.27-23.67-61.78Z">
                  <animateTransform attributeName="transform" type="rotate" values="0 190 270;-10 190 270;10 190 270;-10 190 270;10 190 270;-10 190 270;0 190 270" keyTimes="0;0.12;0.28;0.44;0.6;0.76;1" dur="2.4s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1"/>
                </path>
              </g>
            </g>
          </svg>
        </div>
        <div className="ud-header">
          <div className="ud-header-left">
            <InitialsAvatar
              name={demoUser.nombre || SAFE_DEMO_USER.name}
              baseClass="ud-avatar"
              seed={demoUser.userId}
            />
            <div className="ud-header-text">
              <div className="ud-header-section-name">{t('safe.sectionName')}</div>
              <h1>{greeting}, {firstName}</h1>
              <div className="ud-progress-wrap">
                <div className="ud-progress-meta">
                  <span>{t('safe.surveysAnswered')}</span>
                  <span>{answeredCount} / {totalCount || 5}</span>
                </div>
                <div className="ud-progress-bar">
                  <div className="ud-progress-fill" style={{ width: `${progressPct}%` }}>
                    <span>{progressPct}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="ud-header-right" aria-hidden="true">
            <div className="escena">
              <div className="nube tipo1 nube1" />
              <div className="nube tipo2 nube2" />
              <div className="nube tipo1 nube3" />
              <div className="nube tipo2 nube4" />
              <div className="estrella estrella1" />
              <div className="estrella estrella2" />
              <div className="estrella estrella3" />
              <div className="estrella estrella4" />
              <div className="estrella estrella5" />
              <div className="estrella estrella6" />
              <div className="estrella estrella7" />
              <div className="estrella estrella8" />
              <div className="estrella estrella9" />
              <div className="estrella estrella10" />
              <div className="estrella estrella11" />
              <div className="estrella estrella12" />
              <div className="estrella estrella13" />
              <div className="estrella estrella14" />
              <div className="estrella estrella15" />
              <div className="estrella estrella16" />
              <div className="personaje">
                <div className="ojo izq" />
                <div className="ojo der" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ACT 01 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">01</span>
        <span className="ud-act-label">{t('safe.act01')}</span>
      </div>

      {/* Daily challenge */}
      <div className="ud-daily-challenge">
        <div className="ud-dc-left">
          <div className="ud-dc-mascot">
            <img src="/assets/persona-globo.svg" alt="" className="ud-dc-mascot-img" />
          </div>
        </div>
        <div className="ud-dc-right">
          {/* Profile narrative section */}
          <div className="ud-dc-profile-section">
            <div className="ud-dc-profile-header">
              <div className="ud-icon-box purple ud-icon-box--sm">
                <i className="fas fa-compass" />
              </div>
              <div>
                <h2 className="ud-dc-profile-title">{t('safe.profileTitle')}</h2>
                <small>{t('safe.profileSub')}</small>
              </div>
              <div className={`ud-dc-trend ${trend.className}`}>
                <i className={`fas ${trend.icon}`} /> {trendText}
              </div>
            </div>
            <div className="ud-dc-narrative" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(narrative) }} />
            <div className="ud-dc-evidence">
              <i className="fas fa-shield-alt" />
              {' '}{t('safe.basedOn', { count: totalEvals })}
            </div>
          </div>

          <h2 className="ud-dc-title">"{(action?.translations?.[lang]?.title ?? action?.title) ?? t('safe.defaultChallenge')}"</h2>
          <p className="ud-dc-desc">{(action?.translations?.[lang]?.description ?? action?.description) ?? t('safe.defaultChallengeDesc')}</p>
          {action?.nextLevelText && (
            <div className="ud-dc-nextlevel">
              <i className="fas fa-arrow-up-right-dots" />
              {action.translations?.[lang]?.nextLevelText ?? action.nextLevelText}
            </div>
          )}
          <div className="ud-dc-reinforces">
            <span className="ud-dc-reinforces-label">{t('safe.reinforces')}</span>
            <span className="ud-dc-fund-tag">
              <i className={`fas ${challengeIcon}`} />
              {challengeFund?.shortNames?.[lang] ?? challengeFund?.shortName ?? challengeFund?.names?.[lang] ?? challengeKey}
            </span>
          </div>

          <button
            type="button"
            className={`ud-dc-cta${ctaCommitted ? ' committed' : ''}`}
            onClick={() => setCtaCommitted(true)}
            disabled={ctaCommitted}
          >
            <span className="ud-dc-cta-icon"><i className={ctaCommitted ? 'fas fa-check-circle' : 'fas fa-rocket'} /></span>
            <span className="ud-dc-cta-text">
              {ctaCommitted ? t('safe.committed') : t('safe.tryToday')}
            </span>
            {!ctaCommitted && <span className="ud-dc-cta-arrow"><i className="fas fa-arrow-right" /></span>}
          </button>
        </div>
      </div>

      {/* Kudos carousel */}
      {strengths.length > 0 && (
        <KudosCarousel
          strengths={strengths}
          t={t}
          actionsData={actionsData as ActionItem[]}
          tipsData={tipsData}
        />
      )}

      {/* ── ACT 02 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">02</span>
        <span className="ud-act-label">{t('safe.act02')}</span>
      </div>

      {/* Charts row */}
      <div className="ud-chart-row">
        <div className="ud-card">
          <div className="ud-section-head">
            <div className="ud-icon-box cyan"><i className="fas fa-route" /></div>
            <div>
              <h3>{t('safe.evolution')}</h3>
              <small>{t('safe.evolutionSub')}</small>
            </div>
          </div>
          <JourneyChart userId={demoUser.userId} />
        </div>
        <div className="ud-card">
          <div className="ud-section-head">
            <div className="ud-icon-box pink"><i className="fas fa-fingerprint" /></div>
            <div>
              <h3>{t('safe.radarTitle')}</h3>
              <small>{t('safe.radarSub')}</small>
            </div>
          </div>
          <div className="ud-radar-chart">
            <RadarChart data={radarData} options={radarOptions} icons={radarIcons} />
          </div>
        </div>
      </div>

      {/* ── ACT 03 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">03</span>
        <span className="ud-act-label">{t('safe.act03')}</span>
      </div>

      {/* Fundamentals bar chart */}
      <div className="ud-card ud-fundamentals-card">
        <div className="ud-section-head">
          <div className="ud-icon-box cyan"><i className="fas fa-layer-group" /></div>
          <div>
            <h3>{t('safe.fundamentalsChart')}</h3>
            <small>{t('safe.fundamentalsChartSub')}</small>
          </div>
        </div>
        <div className="fp-bar-chart-scroll">
          <div className="fp-bar-chart">
            {feedbackItems.map(item => {
              const lvl      = pbLevelIndex(item.score);
              const conf     = safeConfidence(item.responses);
              const barPct   = Math.round((item.score / 4) * 100);
              const lvlLabel = t(`safe.level${lvl}`);
              return (
                <FloatingTooltip
                  key={item.id}
                  tooltipClassName="fp-bar-tooltip"
                  content={
                    <>
                      <strong>{item.name}</strong>
                      <span className="fp-bar-tooltip-level" style={{ color: item.color }}>{lvlLabel}</span>
                      <span className="fp-bar-tooltip-meta">{item.responses} {t('safe.peers')} · {conf}</span>
                      <span className="fp-bar-tooltip-cta">
                        <i className="fas fa-expand-alt" /> {t('safe.clickDetail')}
                      </span>
                    </>
                  }
                >
                  {anchorProps => (
                    <div
                      {...anchorProps}
                      className="fp-bar-col"
                      onClick={() => setPopupItem(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => { if (event.key === 'Enter') setPopupItem(item); }}
                      aria-label={item.name}
                    >
                      <span className="fp-bar-score">{formatScore(item.score)}</span>
                      <div className="fp-bar-track">
                        <div className="fp-bar-fill" style={{ height: `${barPct}%`, background: item.color }} />
                      </div>
                      <div className="fp-bar-icon" style={{ background: `${item.color}18`, color: item.color }}>
                        <FundamentalIcon icon={item.icon} imageClassName="fp-bar-icon-img" />
                      </div>
                      <span className="fp-bar-label">{item.name}</span>
                    </div>
                  )}
                </FloatingTooltip>
              );
            })}
          </div>
        </div>
      </div>

      {/* Celebration + Growth */}
      {topFund && topFundObj && (
        <div className="fg-layout">
          {/* Left: Celebration */}
          <div className="fg-left">
            <div className="fg-celebration">
              <div className="fg-celebration-scene">
                <div className="fg-confetti">
                  <span>🎉</span><span>⭐</span><span>✨</span><span>🏆</span><span>🎊</span>
                </div>
                <img
                  src="/assets/persona-torso.svg"
                  alt=""
                  className="fg-celebration-mascot"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              {deltaStr && (
                <div
                  className="fg-celebration-delta"
                  style={{ color: (delta ?? 0) >= 0 ? 'var(--cp-green)' : 'var(--cp-orange)' }}
                >
                  <i className="fas fa-arrow-trend-up" /> {deltaStr}
                </div>
              )}
              <div className="fg-celebration-body">
                <div className="fg-celebration-score">
                  <span className="fg-score-big" style={{ color: celebColor }}>
                    {formatScore(celebScore)}
                  </span>
                  <span className="fg-score-max">/4</span>
                </div>
                <div className="fg-celebration-fund">
                  <span
                    className="fg-fund-badge"
                    style={{ background: `${celebColor}18`, color: celebColor }}
                  >
                    🏅 {celebName.toUpperCase()}
                  </span>
                </div>
                <p className="fg-celebration-phrase">"{celebPhrase}"</p>
              </div>
            </div>
          </div>

          {/* Right: Growth cards */}
          <div className="fg-right">
            <div className="fg-right-header">
              <span className="fg-right-title">{t('safe.growthTitle')}</span>
              <small>{t('safe.growthSub')}</small>
            </div>
            <div className="fg-cards-list">
              {growthList.map(item => {
                const gLvl = pbLevelIndex(item.score);
                const gColor = item.color;
                const gName  = item.name;
                const gLvlLabel = t(`safe.level${gLvl}`);
                // Las acciones siguen catalogadas por fundamental, no por comportamiento.
                const gAction = allActions.find(entry =>
                  entry.fundamentalId === item.fundamentalId && entry.active &&
                  item.score >= entry.threshold && item.score <= entry.thresholdMax,
                ) ?? allActions.find(entry => entry.fundamentalId === item.fundamentalId && entry.active);
                const lvlColors = ['#E11F26', '#F08521', '#40B8E9', '#20B15A'];

                const steps = [0, 1, 2, 3].map(levelIndex => (
                  <div
                    key={levelIndex}
                    className="fg-step"
                    style={levelIndex <= gLvl ? { background: lvlColors[gLvl] } : {}}
                  />
                ));

                return (
                  <div key={item.id} className="fg-card">
                    <div className="fg-card-top">
                      <div
                        className="fg-card-icon"
                        style={{ background: `${gColor}18`, color: gColor }}
                      >
                        <FundamentalIcon
                          icon={item.icon}
                          imageClassName="fund-icon-img fund-icon-img--lg"
                        />
                      </div>
                      <div className="fg-card-info">
                        <h4>{gName}</h4>
                        <span className="fg-card-level" style={{ color: gColor }}>{gLvlLabel}</span>
                      </div>
                      <span className="fg-card-score" style={{ color: gColor }}>
                        {formatScore(item.score)}<small>/4</small>
                      </span>
                    </div>
                    <div className="fg-stepper">{steps}</div>
                    {gAction && (
                      <div className="fg-card-action">
                        <i className="fas fa-bolt" />
                        <span>{gAction.translations?.[lang]?.title ?? gAction.title}</span>
                      </div>
                    )}
                    {gAction?.nextLevelText && (
                      <div className="fg-card-nextlevel">
                        <i className="fas fa-arrow-up-right-dots" />
                        {gAction.translations?.[lang]?.nextLevelText ?? gAction.nextLevelText}
                      </div>
                    )}
                    <span className="fg-card-meta">
                      <i className="fas fa-eye" /> {item.responses} {t('safe.feedbacks')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Given feedback card */}
      <div className="ud-card">
        <div className="ud-section-head">
          <div className="ud-icon-box orange"><i className="fas fa-paper-plane" /></div>
          <div>
            <h3>{t('safe.givenTitle')}</h3>
            <small>{t('safe.givenSub')}</small>
          </div>
        </div>
        <GivenFeedbackList
          answered={answeredQuery.data ?? []}
          isLoading={answeredQuery.isLoading}
          hasError={answeredQuery.isError}
          lang={lang}
        />
      </div>

      {/* Footer */}
      <div className="ud-footer">
        <p><i className="fas fa-lock" /> {t('safe.footerPrivacy')}</p>
        <p><i className="fas fa-heart" /> {t('safe.footerHeart')}</p>
      </div>

      {/* Popup */}
      {popupItem && (
        <FeedbackDetailPopup
          item={popupItem}
          onClose={() => setPopupItem(null)}
          catalogData={catalogData}
          personalData={personalData}
          growthData={growthData}
        />
      )}
    </div>
  );
}
